/**
 * 表单与输入类组件
 * 对应设计稿 .field / .segd / .fstack 与弹窗里的输入框样式。
 */
import type {
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons';

/** 输入框、下拉框共用的外观，抽出来避免三个组件各写一遍 */
const CONTROL_CLASS =
  'h-[38px] w-full rounded-btn border border-line bg-panel-2 px-3 text-body outline-none ' +
  'transition-[border-color,box-shadow] focus:border-brand focus:bg-panel focus:shadow-[0_0_0_3px_var(--color-brand-pale)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60 placeholder:text-ink-3';

/**
 * 搜索框（.field）。带前置图标、行内输入，用于列表页顶部的筛选条。
 * 和下面的 TextField 不同：这个是紧凑型，高 36，不带标签。
 */
export function SearchField({
  icon = 'search',
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { icon?: IconName }) {
  return (
    <label
      className={cn(
        'flex h-9 items-center gap-2 rounded-btn border border-line bg-panel-2 px-3 text-table text-ink-3',
        'focus-within:border-brand focus-within:bg-panel',
        className,
      )}
    >
      <Icon name={icon} size={15} />
      <input
        type="search"
        className="min-w-0 flex-1 border-0 bg-transparent text-table text-ink outline-none placeholder:text-ink-3"
        {...rest}
      />
    </label>
  );
}

/** 带标签的表单项外壳，弹窗里的每个字段都用它包一层 */
export function FormField({
  label,
  hint,
  required,
  children,
  className,
}: {
  label: ReactNode;
  /** 字段下方的补充说明，用来解释业务口径 */
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block min-w-0', className)}>
      <span className="block text-form font-semibold text-ink-2">
        {label}
        {required && <span className="ml-0.5 text-red">*</span>}
      </span>
      <div className="mt-[7px]">{children}</div>
      {hint && <p className="mt-1.5 text-tiny text-ink-3">{hint}</p>}
    </label>
  );
}

export function TextInput({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(CONTROL_CLASS, className)} {...rest} />;
}

/**
 * 下拉选择。
 *
 * 实现搬到了 components/ui/select.tsx：原生 `<select>` 展开的是操作系统样式，
 * 和设计稿完全不搭，而且**不能搜索**——员工、项目、商品动辄几十上百条，
 * 前台收银时只能一条条翻，遇到同名员工更是只能靠位置猜。
 *
 * 这里保留同名导出，是为了让全项目 29 处调用点一行都不用改：
 * 新实现底下压着一个真实的 `<select>`，`value` / `onChange` / `name` /
 * `required` 的行为和原生完全一致，收到的也是真事件而不是伪造对象。
 */
export { Select } from '@/components/ui/select';

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(CONTROL_CLASS, 'h-[78px] resize-none py-2.5', className)}
      {...rest}
    />
  );
}

/** 表单纵向排列（弹窗里的 .fstack） */
export function FormStack({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3.5">{children}</div>;
}

/** 表单两列排列（.frow） */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3.5">{children}</div>;
}

/**
 * 分段控制器（.segd）。
 * 用于「全部订单 / 已完成 / 已退款」这种少量互斥筛选；
 * 选项超过 5 个就该换成下拉框。
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  size?: 'md' | 'lg';
  className?: string;
}) {
  const groupId = useId();
  return (
    <div
      role="tablist"
      aria-label="筛选"
      className={cn(
        'inline-flex gap-0.5 rounded-btn border border-line bg-panel-2 p-[3px]',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            id={`${groupId}-${option.value}`}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-[7px] font-semibold transition-colors',
              size === 'lg'
                ? 'px-4 py-2 text-body'
                : 'px-[13px] py-1.5 text-table',
              active
                ? 'bg-panel text-ink shadow-e1'
                : 'text-ink-2 hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 开关。用于门店设置的能力开关、设置页的是否启用。
 * 做成受控组件，状态由调用方持有，方便和接口的乐观更新配合。
 */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** 无障碍名称；视觉上的文字标签由调用方自己排版 */
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors',
        checked ? 'bg-brand' : 'bg-[#D8D3C8]',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'absolute top-[3px] size-4 rounded-full bg-white shadow-e1 transition-[left]',
          checked ? 'left-[19px]' : 'left-[3px]',
        )}
      />
    </button>
  );
}
